import React from 'react';
import { Button, Card, Input, Select, Space, Tag, Typography } from 'antd';
import type { DeliveryGroupDraft } from './deliveryGroupTypes';
import { DeliveryGroupStopTable, type StopTableContext } from './DeliveryGroupStopTable';
import { filterApplicationsForGroup } from './groupScopeHelpers';

const { Text } = Typography;

type Props = {
  group: DeliveryGroupDraft;
  onChange: (next: DeliveryGroupDraft) => void;
  onRemove?: () => void;
  ctx: Omit<StopTableContext, 'applicationOptions'> & {
    allApplications?: StopTableContext['applications'];
  };
  makeStopUid: () => string;
};

export function DeliveryGroupCard({ group, onChange, onRemove, ctx, makeStopUid }: Props) {
  const unloadingAppOptions = React.useMemo(() => {
    const apps = filterApplicationsForGroup(ctx.allApplications ?? ctx.applications ?? [], group);
    return apps.map((a) => ({
      id: a.id,
      label: `${a.internalNumber || `#${a.id}`}${a.consignee?.name ? ` → ${a.consignee.name}` : ''}`
    }));
  }, [ctx.allApplications, ctx.applications, group]);

  const tableCtx: StopTableContext = {
    ...ctx,
    applicationOptions: unloadingAppOptions
  };

  const title = (
    <Space wrap>
      <Text strong>{group.label || (group.isDefault ? 'Основная delivery group' : 'Delivery group')}</Text>
      {group.isDefault ? <Tag color="blue">Default</Tag> : null}
      {group.applicationId ? <Tag>Заявка #{group.applicationId}</Tag> : null}
      {group.groupId ? <Tag>Group ID {group.groupId}</Tag> : null}
    </Space>
  );

  return (
    <Card
      size="small"
      title={title}
      style={{ marginBottom: 12, borderLeft: group.isDefault ? '3px solid #1677ff' : '3px solid #52c41a' }}
      extra={
        !group.isDefault && onRemove ? (
          <Button type="link" danger size="small" onClick={onRemove}>
            Удалить группу
          </Button>
        ) : null
      }
    >
      {!group.isDefault && (
        <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }}>
          <Text type="secondary">Контекст группы (заявка или склад через точку погрузки)</Text>
          <Select
            style={{ width: 360 }}
            placeholder="Привязать к заявке (опционально)"
            allowClear
            showSearch
            optionFilterProp="children"
            value={group.applicationId ?? undefined}
            onChange={(val) =>
              onChange({
                ...group,
                applicationId: val ?? null,
                label: val
                  ? `Группа — ${ctx.applications?.find((a) => a.id === val)?.internalNumber || val}`
                  : group.label
              })
            }
          >
            {ctx.applications?.map((a) => (
              <Select.Option key={a.id} value={a.id}>
                {a.internalNumber}
                {a.client?.name ? ` — ${a.client.name}` : ''}
              </Select.Option>
            ))}
          </Select>
          <Input
            placeholder="Метка группы"
            value={group.label || ''}
            onChange={(e) => onChange({ ...group, label: e.target.value || null })}
          />
        </Space>
      )}

      {group.isDefault ? (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Основная погрузка/выгрузка задаётся полями формы выше. Ниже — дополнительные точки выгрузки этой группы.
        </Text>
      ) : (
        <>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>
            Погрузка
          </Text>
          <DeliveryGroupStopTable
            stopType="loading"
            stops={group.loadingStops}
            groupUid={group.uid}
            makeStopUid={makeStopUid}
            ctx={tableCtx}
            onChange={(loadingStops) => onChange({ ...group, loadingStops })}
          />
        </>
      )}

      <Text strong style={{ display: 'block', margin: '12px 0 4px' }}>
        Выгрузка
      </Text>
      <DeliveryGroupStopTable
        stopType="unloading"
        stops={group.unloadingStops}
        groupUid={group.uid}
        makeStopUid={makeStopUid}
        ctx={tableCtx}
        onChange={(unloadingStops) => onChange({ ...group, unloadingStops })}
      />
    </Card>
  );
}
